/**
 * CreditsPage — /credits
 *
 * RC 1.35.F3 P1.17 — Credits Visibility Upgrade.
 *
 * Read-only billing dashboard for candidates. Surfaces:
 *   • Included Credits + Top-up Credits summary (data-testid="credits-summary")
 *   • Recent billing transactions (from /api/payments/billing-history)
 *   • CTAs: Add Credits → /account?tab=plan (re-uses the validated
 *     RC 1.34/1.35.F2 top-up flow), Manage billing → Stripe Customer
 *     Portal (data-testid="customer-portal-button")
 *   • Trust strip: "Secure payments by Stripe", "Cancel anytime",
 *     "Transparent billing", "Invoice visibility"
 *
 * Strict rails: read-only — no schema change, no pricing change, no
 * Stripe live flip. Uses the same `apiFetch` + `useCredits` plumbing
 * as the rest of the candidate shell.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Coins, History, Loader2, Plus, ExternalLink, ShieldCheck,
  Receipt, RefreshCw, BadgeCheck, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { useCredits } from '@/lib/creditsContext';
import { isStripeTestMode } from '@/lib/stripeMode';

const KIND_LABEL = {
  usage_topup:        'Top-up Credits',
  plan_subscription:  'Subscription',
};

const PACKAGE_LABEL = {
  usage_20:  '20 Credits',
  usage_50:  '50 Credits',
  usage_120: '120 Credits',
};

const STATUS_BADGE = {
  finalised: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40',
  pending:   'bg-amber-500/10 text-amber-200 border-amber-500/40',
  failed:    'bg-rose-500/10 text-rose-200 border-rose-500/40',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
}

function describeRow(row) {
  if (row.kind === 'usage_topup') {
    const pkg = PACKAGE_LABEL[row.package_code] || row.package_code || 'Top-up';
    return `Top-up Credits · ${pkg}`;
  }
  if (row.kind === 'plan_subscription') {
    const plan = (row.plan_code || 'plan').toString();
    const period = row.billing_period ? ` (${row.billing_period})` : '';
    return `${plan.charAt(0).toUpperCase() + plan.slice(1)} subscription${period}`;
  }
  return KIND_LABEL[row.kind] || row.kind || 'Payment';
}

export default function CreditsPage() {
  const navigate = useNavigate();
  const { refresh: refreshCredits } = useCredits();
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await apiFetch('/api/account/plan');
      setPlan(p);
    } catch (err) {
      toast.error(err?.message || 'Unable to load your Credits balance.');
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const r = await apiFetch('/api/payments/billing-history?limit=10');
      setHistory(Array.isArray(r?.history) ? r.history : []);
    } catch (err) {
      // Surface gracefully — history is optional, the rest of the page
      // is still useful (balance + CTAs).
      setHistoryError(err?.message || 'Unable to load billing history.');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  const onAddCredits = useCallback(() => {
    // Re-use the validated AccountPlanSection top-up flow rather than
    // duplicating the package selector here. The `?addUsage=1` query
    // param auto-opens the package selector on arrival.
    navigate('/account?tab=plan&addUsage=1');
  }, [navigate]);

  const onManageBilling = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await apiFetch('/api/payments/customer-portal', {
        method: 'POST',
        body:   JSON.stringify({}),
      });
      if (!res?.url) throw new Error('Stripe did not return a portal URL.');
      window.location.assign(res.url);
    } catch (err) {
      toast.error(err?.message || 'Unable to open the Stripe billing portal.');
      setPortalLoading(false);
    }
  }, []);

  const included = plan?.included_usage_remaining || 0;
  const extra    = plan?.extra_usage_remaining    || 0;
  const total    = included + extra;
  const planCode = plan?.plan_code || 'base';
  const hasBillingHistory = history.length > 0;

  // Customer Portal is only useful once Stripe has minted a customer
  // for the user — i.e. after the first successful top-up or
  // subscription. We surface this as a hint rather than hiding the
  // button entirely (the backend still 404s gracefully).
  const portalLikelyAvailable = hasBillingHistory;

  const trustItems = useMemo(() => [
    { icon: ShieldCheck,  label: 'Secure payments by Stripe' },
    { icon: BadgeCheck,   label: 'Cancel anytime' },
    { icon: Receipt,      label: 'Transparent billing' },
    { icon: Lock,         label: 'Card details never touch NDORFlow' },
  ], []);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="credits-page">
      <div>
        <h1 className="text-3xl font-serif-display font-semibold text-ndor-ink tracking-tight">
          Credits & Billing
        </h1>
        <p className="text-sm text-ndor-ink-secondary mt-1">
          Your Credits balance, recent payments, and self-service billing
          tools — all in one place.
        </p>
      </div>

      {/* Summary card ─────────────────────────────────────────── */}
      <Card className="border-ndor-edge shadow-sm">
        <CardContent className="p-6" data-testid="credits-summary">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                Available Credits
              </p>
              <p
                className="text-4xl font-serif-display font-semibold text-ndor-ink mt-1 tabular-nums"
                data-testid="credits-summary-total"
              >
                {loading ? '—' : total.toLocaleString()}
              </p>
              <p className="text-xs text-ndor-ink-muted mt-2">
                Plan:{' '}
                <span className="font-medium text-ndor-ink capitalize">
                  {planCode}
                </span>
                {plan?.billing_period && (
                  <> · <span className="text-ndor-ink-secondary">{plan.billing_period}</span></>
                )}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-amber-500/10 text-amber-200">
              <Coins className="h-6 w-6" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 p-4">
              <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                Included Credits
              </p>
              <p
                className="text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums mt-1"
                data-testid="credits-summary-included"
              >
                {loading ? '—' : included.toLocaleString()}
              </p>
              <p className="text-xs text-ndor-ink-muted mt-1">
                Refresh each cycle on Focus / Flow
              </p>
            </div>
            <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 p-4">
              <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                Top-up Credits
              </p>
              <p
                className="text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums mt-1"
                data-testid="credits-summary-extra"
              >
                {loading ? '—' : extra.toLocaleString()}
              </p>
              <p className="text-xs text-ndor-ink-muted mt-1">
                Top-ups · No expiry
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <Button
              onClick={onAddCredits}
              data-testid="plan-add-credits-button"
              className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Credits
            </Button>
            <Button
              variant="outline"
              onClick={onManageBilling}
              disabled={portalLoading}
              data-testid="customer-portal-button"
              className="border-ndor-edge text-ndor-ink hover:bg-ndor-surface"
            >
              {portalLoading
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <ExternalLink className="h-4 w-4 mr-1.5" />}
              Manage billing & invoices
            </Button>
            <Button
              variant="ghost"
              onClick={() => { load(); loadHistory(); refreshCredits?.(); }}
              data-testid="credits-refresh-button"
              className="text-ndor-ink-muted hover:text-ndor-ink"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Refresh
            </Button>
          </div>

          {!portalLikelyAvailable && !loading && (
            <p
              className="text-[11px] text-ndor-ink-muted mt-3"
              data-testid="customer-portal-hint"
            >
              The Stripe billing portal unlocks once you've made your
              first top-up or subscription. Your invoice history will
              appear here after that.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Trust strip ───────────────────────────────────────────── */}
      <Card className="border-ndor-edge shadow-sm">
        <CardContent className="p-4">
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
            data-testid="credits-trust-strip"
          >
            {trustItems.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 text-xs text-ndor-ink-secondary"
              >
                <Icon className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>{label}</span>
              </div>
            ))}
          </div>
          {isStripeTestMode() && (
            <p className="text-[11px] text-ndor-ink-muted mt-3" data-testid="credits-test-mode-hint">
              Stripe is currently in <strong>TEST MODE</strong> — no real
              card is charged. Use Stripe's <code>4242 4242 4242 4242</code> test
              card with any future expiry and CVC.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Billing history ──────────────────────────────────────── */}
      <Card className="border-ndor-edge shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-medium text-ndor-ink">
            <History className="h-5 w-5 text-ndor-ink-muted" />
            Recent payments
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="billing-history-card">
          {historyLoading ? (
            <div className="flex items-center gap-2 text-sm text-ndor-ink-muted py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading payments…
            </div>
          ) : historyError ? (
            <p
              className="text-sm text-rose-200 py-2"
              data-testid="billing-history-error"
            >
              {historyError}
            </p>
          ) : history.length === 0 ? (
            <div
              className="text-sm text-ndor-ink-muted py-2"
              data-testid="billing-history-empty"
            >
              No payments yet. Add Credits to see your invoice history here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="billing-history-table"
              >
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ndor-ink-muted border-b border-ndor-edge">
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="py-2 pr-3 font-medium">Item</th>
                    <th className="py-2 pr-3 font-medium text-right">Amount</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr
                      key={row.session_id || `${row.created_at}-${row.kind}`}
                      data-testid={`billing-history-row-${row.session_id || row.created_at}`}
                      className="border-b border-ndor-edge/40 last:border-b-0"
                    >
                      <td className="py-2 pr-3 text-ndor-ink-secondary whitespace-nowrap">
                        {formatDateTime(row.finalised_at || row.created_at)}
                      </td>
                      <td className="py-2 pr-3 text-ndor-ink">
                        {describeRow(row)}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-ndor-ink tabular-nums">
                        £{row.amount_gbp.toFixed(2)}
                      </td>
                      <td className="py-2">
                        <Badge
                          variant="outline"
                          className={`text-xs font-medium border ${STATUS_BADGE[row.status] || 'bg-ndor-surface-alt text-slate-200 border-slate-200'}`}
                        >
                          {row.status || row.payment_status || 'pending'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-ndor-ink-muted mt-4">
            Need a VAT invoice or refund? Open the Stripe billing portal
            above — every paid transaction has a downloadable receipt.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
